import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

function Projects() {
  const projects = [
    {
      title: 'Downtown Corporate Tower',
      category: 'Commercial Construction',
      image: 'https://images.unsplash.com/photo-1615406020658-6c4b805f1f30?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Modern 15-story office complex with premium finishes'
    },
    {
      title: 'Metro Transit Hub',
      category: 'Civil & Infrastructure',
      image: 'https://images.unsplash.com/photo-1429497419816-9ca5cfb4571a?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Large-scale public infrastructure development'
    },
    {
      title: 'Executive Office Suites',
      category: 'Interior Finishes',
      image: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Luxury interior buildout with custom detailing'
    },
    {
      title: 'Regional Medical Center',
      category: 'Commercial Construction',
      image: 'https://images.unsplash.com/photo-1515263487990-61b07816b324?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'State-of-the-art healthcare facility'
    },
    {
      title: 'Government Facility Renovation',
      category: 'Government Contracting',
      image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Complete renovation of federal building'
    },
    {
      title: 'Financial District Plaza',
      category: 'Commercial Development',
      image: 'https://images.pexels.com/photos/1106476/pexels-photo-1106476.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=800',
      description: 'Mixed-use development with retail and office space'
    }
  ];

  const beforeAfterProjects = [
    {
      title: 'Historic Building Restoration',
      before: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?fm=webp&q=70&auto=format&fit=max&w=800',
      after: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Complete restoration of 1920s commercial building with modern amenities'
    },
    {
      title: 'Office Space Transformation',
      before: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?fm=webp&q=70&auto=format&fit=max&w=800',
      after: 'https://images.unsplash.com/photo-1497366754035-f200968a6e72?fm=webp&q=70&auto=format&fit=max&w=800',
      description: 'Outdated office converted to modern executive workspace'
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Portfolio</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Featured <span className="text-gold">Projects</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-3xl mx-auto">
            Showcasing our commitment to excellence across diverse construction projects nationwide
          </p>
        </div>
      </section>

      {/* Project Gallery Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30" data-testid="projects-section">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-lg cursor-pointer transform hover:scale-105 transition-all duration-300"
                data-testid={`project-${index}`}
              >
                <div className="aspect-w-16 aspect-h-12 bg-gray-900">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-80 object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                    width="600"
                    height="400"
                    style={{ aspectRatio: '3/2' }}
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent opacity-80 group-hover:opacity-95 transition-opacity"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-2 group-hover:translate-y-0 transition-transform">
                  <span className="text-gold text-xs font-semibold uppercase tracking-wider mb-2 block">{project.category}</span>
                  <h4 className="text-white text-2xl font-bold mb-2 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>{project.title}</h4>
                  <p className="text-gray-300 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Before/After Showcase Section */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30" data-testid="before-after-section">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-20">
            <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Transformations</span>
            <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
            <h3 className="text-5xl md:text-6xl font-bold text-white mb-4 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
              Before & <span className="text-gold">After</span>
            </h3>
            <p className="text-gray-300 text-xl max-w-3xl mx-auto">Witness the transformation of spaces through our expert craftsmanship</p>
          </div>
          
          <div className="space-y-16">
            {beforeAfterProjects.map((project, index) => (
              <div 
                key={index}
                className="bg-black/50 backdrop-blur-sm border border-gold/30 rounded-lg p-8 hover:border-gold transition-all"
                data-testid={`before-after-${index}`}
              >
                <h4 className="text-3xl font-bold text-white mb-6 text-center font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>{project.title}</h4>
                <div className="grid md:grid-cols-2 gap-8 mb-6">
                  <div className="pt-44">
                    <div className="relative overflow-hidden rounded-lg group">
                      <img 
                        src={project.before} 
                        alt={`${project.title} - Before`}
                        className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        width="600"
                        height="400"
                        style={{ aspectRatio: '3/2' }}
                      />
                      <div className="absolute top-4 left-4 bg-gold/90 backdrop-blur-sm text-black font-bold px-6 py-2 rounded-full uppercase tracking-wider">
                        Before
                      </div>
                    </div>
                  </div>
                  <div className="pt-44">
                    <div className="relative overflow-hidden rounded-lg group">
                      <img 
                        src={project.after} 
                        alt={`${project.title} - After`}
                        className="w-full h-96 object-cover transition-transform duration-500 group-hover:scale-110"
                        loading="lazy"
                        width="600"
                        height="400"
                        style={{ aspectRatio: '3/2' }}
                      />
                      <div className="absolute top-4 left-4 bg-gold backdrop-blur-sm text-black font-bold px-6 py-2 rounded-full uppercase tracking-wider">
                        After
                      </div>
                    </div>
                  </div>
                </div>
                <p className="text-gray-300 text-lg text-center leading-relaxed">{project.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export default Projects;